import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  AfterViewChecked,
  ViewChild,
} from '@angular/core';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import unescape from 'lodash-es/unescape';
import { AppService } from 'app/app.service';

@Component({
  selector: 'app-dynamic',
  template: ` <div #container class="d-inline" [attr.data-acg-title]="acgUrlTitle"></div> `,
})
export class AcgComponent implements AfterViewChecked, OnDestroy {
  @Input() acgType: string;
  @Input() acgUrlTitle: string;
  @ViewChild('container', { static: true }) container: ElementRef<HTMLDivElement>;

  private destroy$ = new Subject<void>();
  private injectedScripts: HTMLScriptElement[] = [];
  private pendingContent: { html: string; scripts: any[] } | null = null;
  private contentInjected = false;

  constructor(private appService: AppService) {}

  ngAfterViewChecked() {
    if (this.pendingContent && !this.contentInjected) {
      this.injectContent();
    }
  }

  ngOnInit() {
    if (!this.acgType || !this.acgUrlTitle) return;

    this.appService
      .fetchCustomGeneric(this.acgType, this.acgUrlTitle)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((data: { body: string }) => {
        if (data && data.body) {
          const processed = this.processHTML(unescape(data.body));
          this.pendingContent = processed;
        }
      });
  }

  ngOnDestroy() {
    this.cleanupScripts();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private processHTML(html: string): { html: string; scripts: any[] } {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    const scripts: any[] = [];
    tempDiv.querySelectorAll('script').forEach((scriptEl) => {
      scripts.push({
        src: scriptEl.getAttribute('src'),
        content: scriptEl.textContent,
        type: scriptEl.type || 'text/javascript',
      });
      scriptEl.remove();
    });

    return { html: tempDiv.innerHTML, scripts };
  }

  private injectContent() {
    if (!this.pendingContent || !this.container?.nativeElement) return;
    this.contentInjected = true;

    // Inject HTML
    this.container.nativeElement.innerHTML = this.pendingContent.html;

    this.cleanupScripts();

    // Inject new scripts
    this.pendingContent.scripts.forEach((scriptData) => {
      const script = document.createElement('script');
      script.type = scriptData.type;

      if (scriptData.src) {
        script.src = scriptData.src;
      } else if (scriptData.content) {
        script.textContent = scriptData.content;
      }

      this.container.nativeElement.appendChild(script);
      this.injectedScripts.push(script);
    });

    // Clear pending content
    this.pendingContent = null;
  }

  private cleanupScripts() {
    this.injectedScripts.forEach((script) => script.remove());
    this.injectedScripts = [];
  }
}
